import React, { useMemo } from 'react';
import ReactFlow, { Controls, Background, BackgroundVariant, Node, Edge, MarkerType } from 'reactflow';
import dagre from '@dagrejs/dagre';
import 'reactflow/dist/style.css';
import { getEventTypeColor } from './EventTypeIndicator';
import { STORE_COLORS as SHARED_STORE_COLORS } from '../pages/explorer/storeColors';

interface EventItem {
  metadata?: {
    eventId?: string;
    eventType?: string;
    timestamp?: string;
    sequence?: number;
    causedBy?: string | string[] | null;
    source?: string;
    [key: string]: unknown;
  };
  data?: Record<string, unknown>;
}

interface CausalityDagProps {
  events: EventItem[];
  storeName?: string;
  /** For cross-store: map of eventId -> storeName */
  storeMap?: Record<string, string>;
  height?: number;
}

const STORE_COLORS: Record<string, string> = {
  // Target-state store colors come from the shared module so DAG + timeline
  // + explorer badges always agree. See pages/explorer/storeColors.ts.
  ...SHARED_STORE_COLORS,
  // Legacy pre-restoration store colors kept locally — those stores are
  // gone from the target-state demo but historical DAGs may still reference
  // them, and the shared module intentionally scopes to target-state only.
  'demo-transactions': '#1976d2',
  'demo-risk-assessments': '#e65100',
  'demo-merchant-ledger': '#2e7d32',
};

function getNodeColor(eventType: string, store?: string): string {
  if (store && STORE_COLORS[store]) return STORE_COLORS[store];
  return getEventTypeColor(eventType);
}

function layoutDag(nodes: Node[], edges: Edge[]): Node[] {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 60, ranksep: 80 });

  nodes.forEach((n) => g.setNode(n.id, { width: 180, height: 50 }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);

  return nodes.map((n) => {
    const pos = g.node(n.id);
    return { ...n, position: { x: pos.x - 90, y: pos.y - 25 } };
  });
}

export function CausalityDag({ events, storeName, storeMap, height = 500 }: CausalityDagProps) {
  const { nodes, edges } = useMemo(() => {
    const nodeList: Node[] = [];
    const edgeList: Edge[] = [];
    const eventIds = new Set(events.map((e) => e.metadata?.eventId).filter(Boolean));

    events.forEach((e) => {
      const id = e.metadata?.eventId;
      if (!id) return;

      const eventType = e.metadata?.eventType || 'unknown';
      const store = storeMap?.[id] || storeName || '';
      const color = getNodeColor(eventType, store);

      nodeList.push({
        id,
        data: {
          label: (
            <div style={{ textAlign: 'center', fontSize: 11 }}>
              <div style={{ fontWeight: 700, color }}>{eventType}</div>
              {store && <div style={{ fontSize: 9, color: '#666' }}>{store}</div>}
              <div style={{ fontSize: 9, color: '#999' }}>
                seq {e.metadata?.sequence}
              </div>
            </div>
          ),
        },
        position: { x: 0, y: 0 },
        style: {
          border: `2px solid ${color}`,
          borderRadius: 8,
          padding: '4px 8px',
          background: '#fff',
          width: 180,
        },
      });

      // Create edges from causedBy
      const causedBy = e.metadata?.causedBy;
      const parents = causedBy ? (Array.isArray(causedBy) ? causedBy : [causedBy]) : [];
      parents.forEach((parentId) => {
        if (eventIds.has(parentId)) {
          edgeList.push({
            id: `${parentId}->${id}`,
            source: parentId,
            target: id,
            animated: true,
            style: { stroke: color },
            markerEnd: { type: MarkerType.ArrowClosed, color },
          });
        }
      });
    });

    const laidOut = layoutDag(nodeList, edgeList);
    return { nodes: laidOut, edges: edgeList };
  }, [events, storeName, storeMap]);

  if (events.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: '#687078' }}>No events to display</div>;
  }

  return (
    <div style={{ height, border: '1px solid #d5dbdb', borderRadius: 8, overflow: 'hidden' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        attributionPosition="bottom-left"
        nodesDraggable={false}
        nodesConnectable={false}
      >
        <Controls showInteractive={false} />
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
      </ReactFlow>
    </div>
  );
}
